import React, { useEffect, useRef, useState } from "react";

/**
 * Top-of-page navigation progress bar for the Lab.
 *
 * Listens for two custom window events:
 *   - "lab:nav-start" → climb from 0 toward 90% asymptotically
 *   - "lab:nav-end"   → snap to 100, fade out
 *
 * Also auto-starts on "popstate" so direct URL changes / back-button get the
 * indicator. Auto-ends 4s after start as a safety net so the bar never gets
 * stuck if a page forgets to fire nav-end.
 *
 * Pages can drive their own loading state with the useLabPageReady() hook
 * exported below: it fires lab:nav-end when `loading` flips to false.
 */
const LabProgressBar = () => {
    const [progress, setProgress] = useState(0);
    const [visible, setVisible] = useState(false);
    const tickRef = useRef(null);
    const safetyRef = useRef(null);
    const fadeRef = useRef(null);

    useEffect(() => {
        const clearAll = () => {
            if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
            if (safetyRef.current) { clearTimeout(safetyRef.current); safetyRef.current = null; }
            if (fadeRef.current) { clearTimeout(fadeRef.current); fadeRef.current = null; }
        };

        const start = () => {
            clearAll();
            setVisible(true);
            setProgress(8);
            tickRef.current = setInterval(() => {
                setProgress((p) => (p >= 90 ? p : p + (90 - p) * 0.12));
            }, 180);
            safetyRef.current = setTimeout(() => stop(), 4000);
        };

        const stop = () => {
            clearAll();
            setProgress(100);
            fadeRef.current = setTimeout(() => {
                setVisible(false);
                setProgress(0);
            }, 220);
        };

        window.addEventListener("lab:nav-start", start);
        window.addEventListener("lab:nav-end", stop);
        window.addEventListener("popstate", start);
        return () => {
            window.removeEventListener("lab:nav-start", start);
            window.removeEventListener("lab:nav-end", stop);
            window.removeEventListener("popstate", start);
            clearAll();
        };
    }, []);

    if (!visible) return null;

    return (
        <div
            aria-hidden="true"
            className="fixed top-0 left-0 right-0 z-[60] h-0.5 pointer-events-none"
        >
            <div
                className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-pink-500 shadow-[0_0_10px_rgba(99,102,241,0.7)] transition-[width,opacity] duration-200 ease-out"
                style={{
                    width: `${progress}%`,
                    opacity: progress >= 100 ? 0 : 1,
                }}
            />
        </div>
    );
};

/**
 * Hook for pages that want to extend the progress bar until their initial
 * data load completes. Pass the page's loading boolean; when it flips
 * false (or when the component unmounts), nav-end fires.
 *
 *   const { loading } = useSomeData();
 *   useLabPageReady(loading);
 */
export const useLabPageReady = (loading) => {
    useEffect(() => {
        if (!loading) {
            window.dispatchEvent(new Event("lab:nav-end"));
        }
        return () => {
            // Cleanup also fires nav-end in case the page unmounts mid-load
            window.dispatchEvent(new Event("lab:nav-end"));
        };
    }, [loading]);
};

export default LabProgressBar;
